<template>
  <v-container>
    <div class="d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <v-checkbox color="primary" v-model="showAllData" :label="$t('extended_route_data')"></v-checkbox>
        <info-modal class="ml-1" small>
          <v-card-title>
            <h4>{{ $t('extended_route_data') }}</h4>
          </v-card-title>
          <v-card-text>
            <span v-html="$t('extended_route_data_help')"/>
          </v-card-text>
        </info-modal>
        <!-- VF-4344: opt in to one row per reading instead of one merged row per minute -->
        <v-checkbox class="ml-6" color="primary" v-model="expandMinuteRows" :label="$t('expand_minute_rows')"></v-checkbox>
        <info-modal class="ml-1" small>
          <v-card-title>
            <h4>{{ $t('expand_minute_rows') }}</h4>
          </v-card-title>
          <v-card-text>
            <span v-html="$t('expand_minute_rows_help')"/>
          </v-card-text>
        </info-modal>
      </div>
      <table-pagination-controls
        :total-rows="totalRows"
        :current-page.sync="currentPage"
        :rows-per-page.sync="rowsPerPage"
        :per-page-options="pageOptions"/>
      <div></div>
    </div>
    <v-data-table
      hide-default-footer
      class="vf-table mt-2"
      :headers="tableFields"
      :items="displayedData"
      :page="currentPage"
      :items-per-page="rowsPerPage"
      :loading="isBusy"
      :loading-text="$t('loading')"
      :item-class="rowClassCallback">

      <!-- Only a stateless button lives in the header: Vuetify renders the
           header `<th>`s without keys, so this node is destroyed and
           re-created whenever a column is hidden or shown. The configurator
           itself is mounted below, outside the table, so the open dialog
           survives a toggle. -->
      <template v-slot:header.__columnConfigurator>
        <v-btn
          icon
          small
          class="column-configurator-activator"
          :aria-label="$t('configure_columns')"
          @click.stop="openColumnConfigurator">
          <v-icon small>mdi-cog</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.timestamp="row">
        <multi-time-display :time="row.value" :time-format="timestampFormat"/>
      </template>

      <!--
        VF-4353: all product columns must share ONE v-for slot block. Vue 2
        keys scoped slots by the raw slot-name expression text, so two sibling
        v-for blocks that both compile to `item.${column.value}` silently
        collapse to the last block (no warning) and the first zone's columns
        fall back to Vuetify's default cell: the raw Celsius number.
      -->
      <template v-for="column in productColumns" v-slot:[`item.${column.value}`]="row">
        <span :key="column.value" v-if="row.item[column.value] != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>

      <template v-slot:item.cooler="row">
        <span v-if="row.item.cooler != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>

      <template v-slot:item.freezer="row">
        <span v-if="row.item.freezer != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>

      <template v-slot:item.freezerSetPoint="row">
        <span v-if="row.item.freezerSetPoint != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>
      <template v-slot:item.coolerSetPoint="row">
        <span v-if="row.item.coolerSetPoint != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>

      <template v-slot:item.unitPower="row">
        <span v-if="row.item.unitPower != null">{{ row.item.unitPower ? 'On' : 'Off' }}</span>
      </template>
      <template v-slot:item.ambientTemp="row">
        <span v-if="row.item.ambientTemp != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>
      <template v-slot:item.freezerDischargeAir="row">
        <span v-if="row.item.freezerDischargeAir != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>
      <template v-slot:item.coolerDischargeAir="row">
        <span v-if="row.item.coolerDischargeAir != null">
          {{ temperatureToCustomerScale(row.value) }} &deg; {{ customerContext.tempFormat() }}
        </span>
      </template>
      <template v-slot:item.door1="row">
        <span v-if="row.item.door1 != null">{{ row.item.door1 ? 'Open' : 'Closed' }}</span>
      </template>
      <template v-slot:item.door2="row">
        <span v-if="row.item.door2 != null">{{ row.item.door2 ? 'Open' : 'Closed' }}</span>
      </template>
      <template v-slot:item.alarmCode="row">
        <span v-if="row.item.alarmCode != null">
          {{ row.item.alarmCode }}<template v-if="row.item.alarmDescription"> - {{ row.item.alarmDescription }}</template>
        </span>
      </template>

      <template v-slot:item.created="row">
        <multi-time-display v-if="row.value" :time="row.value" :time-format="timestampFormat"/>
      </template>

      <template v-slot:item.gps="row">
        <a
          v-if="row.item && (row.item.lat != null && row.item.long != null)"
          :href="`https://www.google.com/maps?q=${row.item.lat.toFixed(6)},${row.item.long.toFixed(6)}`">
          <v-icon color="primary">mdi-earth</v-icon>
          {{ row.item.lat.toFixed(4) }} , {{ row.item.long.toFixed(4) }}
        </a>
      </template>

      <template v-slot:item.location="row">
        <router-link v-if="row.value" :to="`/location/update/${row.value.id}`">
          {{ row.value.label }}
        </router-link>
      </template>

      <template v-slot:item.status="row">
        <vf-tooltip v-if="row.item.warning_icon" :text="row.item.warning_text" :max-width="300" :open-delay="100">
          <v-icon color="warning">mdi-alert</v-icon>
        </vf-tooltip>
      </template>

    </v-data-table>
    <!-- Mounted outside the table on purpose (see the header slot above):
         this node's position never changes, so the dialog stays open while
         the user toggles and reorders columns. Renders nothing while the
         feature flag is off. -->
    <column-configurator
      ref="columnConfigurator"
      hide-activator
      :table-id="tableId"
      :columns="resolvedColumns"
      :default-columns="defaultColumnRegistry"
      @update:columns="onColumnsUpdate"
      @reset="onColumnsReset" />
  </v-container>
</template>

<script>
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import InfoModal from '@/components/shared/InfoModal'
import VfTooltip from '@/components/shared/VfTooltip'
import ColumnConfigurator, { COLUMN_CONFIGURATOR_HEADER } from '@/components/shared/ColumnConfigurator'
import TableColumnPreferencesMixin from '@/components/mixins/TableColumnPreferencesMixin'
import { resolveColumns } from '@/util/tableColumnPreferences'
import Moment from 'moment-timezone'

import { map, filter, upperFirst, each, size, some } from 'lodash'

// TracKing telemetry columns (VF-4331). Each `value` is both the Vuetify header
// value and the column id user preferences are saved under (VF-4448), so it is
// frozen: renaming one orphans every saved preference for that column.
// VF-4351: the order below is TracKing's own export order — unit-wide readings
// first, then the freezer block, then the cooler block. It is the DEFAULT
// on-screen order (users may reorder these columns in the configurator) and
// must stay identical to RouteController::telemetryExportColumns() in
// app/api/modules/v2/controllers/RouteController.php, whose export order does
// not follow on-screen preferences. VF-4342 inserted freezerSetPoint right
// after freezerOpStatus and coolerSetPoint right after coolerOpStatus.
const TELEMETRY_COLUMNS = [
  { value: 'unitPower', text: 'Unit Power', sortable: false },
  { value: 'unitMode', text: 'Unit Mode', sortable: false },
  { value: 'ambientTemp', text: 'Ambient Temp', sortable: false },
  { value: 'freezerOpStatus', text: 'Freezer Op Status', sortable: false },
  { value: 'freezerSetPoint', text: 'Freezer Set Point', sortable: false },
  { value: 'freezerDischargeAir', text: 'Freezer Discharge Air', sortable: false },
  { value: 'door1', text: 'Door 1', sortable: false },
  { value: 'coolerOpStatus', text: 'Cooler Op Status', sortable: false },
  { value: 'coolerSetPoint', text: 'Cooler Set Point', sortable: false },
  { value: 'coolerDischargeAir', text: 'Cooler Discharge Air', sortable: false },
  { value: 'door2', text: 'Door 2', sortable: false },
  { value: 'alarmCode', text: 'Alarm', sortable: false }
]

/**
 * Persistence contract for this table (epic VF-4136). Both values are FIXED:
 * the id is what saved preferences are keyed by, so renaming it orphans every
 * preference already stored, and the integer version is bumped only for a
 * deliberate migration of the registry below.
 */
export const ROUTE_DATA_TABLE_ID = 'RouteDataTable'
export const ROUTE_DATA_COLUMNS_VERSION = 1

export default {
  name: 'RouteDataTable',
  components: {
    TablePaginationControls,
    MultiTimeDisplay,
    InfoModal,
    VfTooltip,
    ColumnConfigurator
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin,
    TableColumnPreferencesMixin
  ],
  props: {
    route: Object
  },
  data () {
    return {
      routeData: {},
      isBusy: false,
      showAllData: false,
      expandMinuteRows: false,
      currentPage: 1,
      rowsPerPage: 10,
      pageOptions: [10, 20, 50, 100, 200, 500],
      /**
       * VF-4448: true once the first sensor-data request has finished (success
       * or failure), i.e. once the telemetry columns in the registry reflect
       * this route's readings. Never reset: a later refetch (expandMinuteRows)
       * keeps the previous readings on screen until the new ones arrive.
       */
      columnRegistryReady: false
    }
  },
  methods: {
    /**
     * Open the configurator modal from the gear button rendered in the table
     * header. The modal itself lives outside the table (see the template) so
     * it is not re-created when the header row changes length.
     */
    openColumnConfigurator () {
      this.$refs.columnConfigurator.openDialog()
    },
    rowClassCallback (row) {
      return row.css_class
    },
    async refreshResults () {
      this.isBusy = true
      try {
        const url = this.$orm('Route').endpoint + '/' + this.route.id + '/sensor-data'
        const response = await this.$httpAdapter.get(url, {
          params: {
            gps: true,
            locations: true,
            // VF-4344: only when expanding, ask for one row per reading with
            // second precision; otherwise the request is the legacy one and
            // the API merges same-minute readings as it always has.
            ...(this.expandMinuteRows ? { seconds: true } : {}),
            expand_timeline_by: 'PT2H'
          }
        })
        this.routeData = map(response.data.data, x => {
          // Add some extra metadata if the data is early or late
          if (x.timeline_period != null) {
            x.css_class = 'disabled'
          }
          // Also add extra metadata if the data was delayed
          if (Moment(x.created).diff(x.timestamp, 'minutes') > this.telemetryDelayWarningTime) {
            x.css_class = 'highlight-warning'
            x.warning_icon = 'mdi-stopwatch'
            x.warning_text = this.$t(
              'delayed_telemetry_tooltip',
              { human_time_diff: `${this.telemetryDelayWarningTime} minutes` }
            )
          }
          return x
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.columnRegistryReady = true
      }
    }
  },
  computed: {
    totalRows () {
      return size(this.displayedData)
    },
    from () {
      return this.currentPage * this.rowsPerPage - (this.rowsPerPage - 1)
    },
    to () {
      return this.currentPage * this.rowsPerPage
    },
    displayedData () {
      if (this.showAllData) {
        return this.routeData
      } else {
        return filter(this.routeData, row => row.timeline_period == null)
      }
    },
    coolerProductColumns () {
      return map(this.route.zone_indexed_route_products.cooler, rp => {
        return { value: `product_${rp.product_id}`, text: rp.product.name }
      })
    },
    freezerProductColumns () {
      return map(this.route.zone_indexed_route_products.freezer, rp => {
        return { value: `product_${rp.product_id}`, text: rp.product.name }
      })
    },
    // VF-4353: single source for the per-product slot v-for (see the template
    // comment); tableFields keeps using the zone-specific computeds for order.
    productColumns () {
      return [...this.coolerProductColumns, ...this.freezerProductColumns]
    },
    /**
     * VF-4331: telemetry columns are TracKing-sourced; hide any column no
     * data point has a value for, so non-TracKing customers see no change.
     */
    telemetryFields () {
      return filter(TELEMETRY_COLUMNS, column =>
        some(this.routeData, row => row[column.value] != null))
    },
    /**
     * @return String the id saved preferences for this table are keyed by
     */
    tableId () {
      return ROUTE_DATA_TABLE_ID
    },

    /**
     * This table's built-in column registry in default display order:
     * Timestamp pinned first, then each zone's products ahead of that zone's
     * air column, then the TracKing telemetry columns, then Received / GPS /
     * Location / Status. `id` always equals the Vuetify header `value`;
     * product ids are product_{product_id} (VF-4177) so a preference follows
     * the product across every route that carries it. `sortable` rides along
     * as an extra field — the configurator preserves unknown fields untouched.
     *
     * VF-4448: a telemetry column is registered only when some loaded reading
     * carries a value for it (telemetryFields, VF-4331), so the registry
     * differs per route and has no telemetry columns until the readings
     * arrive. Saved ids the current registry lacks are kept as dormant entries
     * (VF-4416) and apply again when the column returns; tableColumnDefaults
     * publishes the registry only once the readings have loaded.
     *
     * @return Array
     */
    defaultColumnRegistry () {
      const columns = [{
        id: 'timestamp',
        label: this.$t('timestamp'),
        visible: true,
        primary: true,
        hideable: false,
        sortable: true
      }]
      each(this.coolerProductColumns, column => {
        columns.push({ id: column.value, label: column.text, visible: true, primary: false, hideable: true })
      })
      columns.push({ id: 'cooler', label: upperFirst(this.$t('cooler')), visible: true, primary: false, hideable: true })
      each(this.freezerProductColumns, column => {
        columns.push({ id: column.value, label: column.text, visible: true, primary: false, hideable: true })
      })
      columns.push({ id: 'freezer', label: upperFirst(this.$t('freezer')), visible: true, primary: false, hideable: true })
      each(this.telemetryFields, column => {
        columns.push({ id: column.value, label: column.text, visible: true, primary: false, hideable: true })
      })
      columns.push({ id: 'created', label: this.$t('received'), visible: true, primary: false, hideable: true, sortable: true })
      columns.push({ id: 'gps', label: 'GPS', visible: true, primary: false, hideable: true })
      columns.push({ id: 'location', label: this.$tc('location'), visible: true, primary: false, hideable: true })
      columns.push({ id: 'status', label: this.$t('status'), visible: true, primary: false, hideable: true })
      return columns
    },

    /**
     * Override of TableColumnPreferencesMixin's computed: the single table
     * this component owns.
     *
     * VF-4448: published only once the first sensor-data request has finished
     * (columnRegistryReady). Before that the registry has no telemetry
     * columns, and publishing it would let the mixin's mount-time convergence
     * (or an early edit) pin a column list without them: the telemetry
     * columns would stay off the table for the rest of the visit, and the
     * next write would drop their saved preferences. With nothing published,
     * that convergence and the update/reset handlers are no-ops for this table.
     *
     * @return Object
     */
    tableColumnDefaults () {
      if (!this.columnRegistryReady) {
        return {}
      }
      return {
        [ROUTE_DATA_TABLE_ID]: {
          version: ROUTE_DATA_COLUMNS_VERSION,
          columns: this.defaultColumnRegistry
        }
      }
    },

    /**
     * The registry actually in effect: saved order/visibility merged into the
     * defaults, or the raw defaults when the feature flag is off.
     *
     * While the readings load nothing is published (see tableColumnDefaults),
     * so the saved preferences are resolved here, read-only: the columns that
     * already exist keep the user's order and visibility, and nothing can be
     * written from this path.
     *
     * @return Array
     */
    resolvedColumns () {
      const resolved = this.resolvedTableColumns[ROUTE_DATA_TABLE_ID]
      if (resolved) {
        return resolved
      }
      if (!this.configurableTableColumnsEnabled) {
        return this.defaultColumnRegistry
      }
      return resolveColumns(
        this.defaultColumnRegistry,
        this.storedTableColumns[ROUTE_DATA_TABLE_ID],
        ROUTE_DATA_COLUMNS_VERSION
      ).columns
    },

    /**
     * Vuetify headers for the visible registry columns, in the effective
     * order, with the synthetic gear header appended once the feature is on
     * and the readings have loaded (VF-4197, VF-4448), so the configurator is
     * never opened on a column list still missing the telemetry columns.
     *
     * @return Array
     */
    tableFields () {
      const fields = []
      each(this.resolvedColumns, column => {
        if (!column.visible) {
          return
        }
        fields.push({ value: column.id, text: column.label, sortable: column.sortable === true })
      })
      if (this.configurableTableColumnsEnabled && this.columnRegistryReady) {
        fields.push(COLUMN_CONFIGURATOR_HEADER)
      }
      return fields
    },
    /**
     * produce warnings when the telemetry is delayed by more than half the
     * data_gap_fatal_duration value
     *
     * @return number
     */
    telemetryDelayWarningTime () {
      return this.customerContext.getSetting('data_gap_fatal_duration', 40) / 2
    },
    /**
     * VF-4344: merged rows are minute-precision, so the default format is
     * enough; expanded rows differ only by seconds and need them shown.
     * Undefined lets MultiTimeDisplay fall back to its own default.
     *
     * @return string|undefined
     */
    timestampFormat () {
      return this.expandMinuteRows ? 'MMM D, YYYY h:mm:ss A' : undefined
    }
  },
  watch: {
    // If rowsPerPage changes, reset currentPage to 1
    rowsPerPage () {
      this.currentPage = 1
    },
    showAllData () {
      this.currentPage = 1
    },
    // The merged and the expanded views are different API responses, so
    // toggling has to refetch rather than filter what is already loaded.
    expandMinuteRows () {
      this.currentPage = 1
      this.refreshResults()
    }
  },
  mounted () {
    this.$gtag.event('show_telemetry', { event_category: 'Route' })
    this.refreshResults()
  }
}
</script>

<style scoped>

</style>
